import React, { FunctionComponent, useContext, useEffect } from 'react';
import { useAppSelector } from 'hooks/redux';
import { nextLiveBroadcastSelector } from 'features/broadcast/selectors';
import { eventSelector } from 'features/events/eventsSelectors';
import { Icon } from 'components/Icons';
import { Link } from 'react-router-dom';
import { Loader } from 'components/Loader';
import { WebSocketContext } from 'features/hub/hub';

export const CurrentlyLiveWidget: FunctionComponent = () => {
  const currentState = useAppSelector((state) => state);
  const liveBroadcast = nextLiveBroadcastSelector(currentState);
  const liveEvent = eventSelector(
    currentState,
    liveBroadcast?.eventId || undefined
  );
  const isLoaded = useAppSelector((state) => state.channel.isLoaded);
  const isHubConnected = useAppSelector((state) => state.hub.isHubConnected);
  const { subscribeToBroadcast } = useContext(WebSocketContext);

  useEffect(
    function subscribeToHubBroadcast() {
      if (isHubConnected && liveBroadcast) {
        subscribeToBroadcast(liveBroadcast.uid);
      }
    },
    [liveBroadcast, isHubConnected, subscribeToBroadcast]
  );

  if (!isLoaded) {
    return (
      <div className="tw-h-[24px] tw-w-full [&_svg]:tw-h-[12px] [&_svg]:tw-w-[12px] [&_svg]:tw-fill-label tw-relative">
        <Loader fillComponent={true} />
      </div>
    );
  }

  if (!liveEvent) {
    return (
      <div className="tw-h-[24px] tw-text-center">
        <div className="tw-bg-mixlr-orange/20 tw-inline-flex tw-align-top tw-max-w-[28rem] tw-mx-auto tw-font-bold tw-h-[24px] tw-space-x-2 tw-text-mixlr-orange tw-items-center tw-justify-center tw-text-[10px] tw-rounded-full tw-px-4">
          <Icon
            hidden={true}
            icon={'offAir'}
            label={''}
            height={10}
            width={10}
          />
          <span>Currently Off-Air</span>
        </div>
      </div>
    );
  }

  return (
    <div className="tw-h-[24px] tw-text-center">
      <Link
        to={`/events/${liveEvent.id}`}
        target="_blank"
        data-testid="event-link"
        className="tw-bg-mixlr-red-20 tw-inline-flex tw-align-top mx-auto tw-max-w-[28rem] tw-font-bold tw-h-[24px] tw-text-mixlr-red hover:tw-text-mixlr-red tw-items-center tw-justify-center tw-space-x-3 tw-text-[10px] tw-rounded-full tw-pr-4"
        title={`Currently live with: ${liveEvent.title}`}
      >
        <div className="tw-flex-none tw-bg-mixlr-red-20 tw-flex tw-items-center tw-rounded-full tw-leading-none">
          <div className="tw-text-white tw-bg-mixlr-red tw-flex tw-items-center tw-justify-center tw-rounded-full tw-h-[24px] tw-space-x-2 tw-flex-none tw-pr-4 tw-pl-3">
            <Icon
              hidden={true}
              icon={'liveNow'}
              label={''}
              height={10}
              width={10}
            />
            <span data-testid="event-type">
              {liveEvent.fallback ? 'Fallback' : 'Live Event'}
            </span>
          </div>
        </div>
        <div className="tw-flex-1 tw-flex tw-items-center tw-justify-between [&_svg]:tw-ml-2 tw-overflow-hidden">
          <div data-testid="event-title" className="tw-block tw-truncate">
            {liveEvent.title}
          </div>
          <Icon
            hidden={true}
            icon={'external'}
            label={''}
            height={12}
            width={12}
          />
        </div>
      </Link>
    </div>
  );
};
